import { ClaimAccessStatus } from '../components/ClaimAccessStatus';
import { useClaimMembership } from "./useClaimMembership";
import { ClaimStep6, ClaimStep1, ClaimStep4, ClaimStep3, ClaimStep5, ClaimStep7, ClaimConfirmation, ClaimStep2, Notice } from "./ClaimMembershipSections";
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, Pencil, Camera, IdCard, ClipboardCheck, Clock, CreditCard } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
// pre-fix single slot (read once, then removed)
// { [memberNumber]: session } - one saved claim per member, never shared across people

const STEPS = [{
  n: 1,
  label: 'Review details',
  icon: ClipboardCheck
}, {
  n: 2,
  label: 'Complete information',
  icon: Pencil
}, {
  n: 3,
  label: 'Photo ID',
  icon: IdCard
}, {
  n: 4,
  label: 'Membership photo',
  icon: Camera
}, {
  n: 5,
  label: 'Review & submit',
  icon: CheckCircle2
}, {
  n: 6,
  label: 'Verification',
  icon: Clock
}, {
  n: 7,
  label: 'Digital card',
  icon: CreditCard
}];
const Stepper = ({
  current
}) => <ol className="grid grid-cols-7 gap-1 sm:gap-2" data-testid="claim-stepper" aria-label="Progress">
    {STEPS.map(s => {
    const Icon = s.icon;
    const state = s.n < current ? 'done' : s.n === current ? 'current' : 'todo';
    return <li key={s.n} className="flex flex-col items-center text-center min-w-0">
          <span className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center border-2 transition-colors duration-200 ${state === 'done' ? 'bg-[hsl(var(--lux-royal))] border-[hsl(var(--lux-royal))] text-white' : state === 'current' ? 'bg-[hsl(var(--lux-gold))] border-[hsl(var(--lux-gold))] text-white' : 'bg-white border-[hsl(var(--lux-royal)/0.2)] text-[hsl(var(--lux-ink)/0.4)]'}`}>
            {state === 'done' ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
          </span>
          <span className={`mt-1.5 text-[10px] sm:text-xs leading-tight ${state === 'current' ? 'font-semibold text-[hsl(var(--lux-royal))]' : 'text-[hsl(var(--lux-ink)/0.55)]'} hidden sm:block`}>{s.label}</span>
        </li>;
  })}
  </ol>;

// Phone number keypads often have no "/" key: accept digits and insert the slashes automatically (DD/MM/YYYY).

function ClaimMembershipFlow() {
  const {
    busy,
    confirm,
    confirmError,
    confirmValue,
    detailsError,
    error,
    findAddress,
    form,
    guardian,
    idFiles,
    intro,
    isMissing,
    linkReq,
    lookup,
    member,
    missing,
    phase,
    photoFiles,
    pickAddress,
    requestLink,
    saveDetails,
    saveGuardian,
    setConfirmError,
    setConfirmValue,
    setField,
    setForm,
    setGuardian,
    setIdFiles,
    setLinkReq,
    setPhotoFiles,
    setStep,
    step,
    stepFor,
    submit,
    token,
    upload
  } = useClaimMembership();
  // NOTE: inputs use the module-level ClaimField component. A component defined inside this function made React
  // remount the input on every keystroke, which closed the phone keyboard after one character.

  const shell = children => <div className="min-h-[80vh] bg-[hsl(var(--lux-ivory))] py-8 sm:py-12 px-4" data-testid="claim-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <p className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--lux-gold))]">Gujarat Samaj Nottingham</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[hsl(var(--lux-royal))] mt-1" style={{
          fontFamily: 'Cormorant Garamond'
        }}>Claim your existing membership</h1>
        </div>
        {(phase === 'flow' || phase === 'confirm') && <ClaimAccessStatus activation={member?.activation || intro?.activation} progress={member?.claim_progress || intro?.claim_progress} />}
        {children}
        <p className="mt-6 text-center text-sm text-[hsl(var(--lux-ink)/0.55)]">Need a hand? <Link to="/contact" className="underline text-[hsl(var(--lux-royal))]">Contact us</Link> — we are always happy to help.</p>
        <p className="mt-2 text-center text-xs text-[hsl(var(--lux-ink)/0.5)]">How we look after your information, including documents and photographs: <Link to="/privacy-policy" className="underline text-[hsl(var(--lux-royal))]" data-testid="claim-privacy-link">Privacy Policy</Link>.</p>
      </div>
    </div>;
  if (phase === 'loading') return shell(<div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[hsl(var(--lux-royal))]" /></div>);
  if (phase === 'invalid' || phase === 'used') {
    const isTestLink = (token || '').startsWith('preview-only');
    return shell(<Card className="border-0 shadow-xl rounded-2xl"><CardContent className="p-7 sm:p-9 space-y-5">
        {isTestLink ? <Notice testId="claim-test-link"><p className="font-semibold">This button came from a test email</p><p className="mt-1">Test emails are sent to administrators to check the wording, so their buttons are not live. Every real invitation contains a personal link that works. If you are a member and would like your own link, please use the form below.</p></Notice> : <Notice tone="warn" testId="claim-link-problem"><p className="font-semibold">{phase === 'used' ? 'This link has already been used' : 'This link is not valid or has expired'}</p><p className="mt-1">{error}</p></Notice>}
        {linkReq.done ? <Notice tone="good" testId="claim-link-requested">Thank you. If those details match our records, a fresh secure link is on its way to that email address. Please check your inbox (and your junk folder) in a few minutes.</Notice> : <div className="space-y-4">
            <p className="text-base text-[hsl(var(--lux-ink))]">Please enter your GS membership number and the email address we hold for you, and we will send you a fresh secure link.</p>
            <div className="space-y-1.5"><Label className="text-base">GS membership number</Label><Input className="h-12 text-base" data-testid="claim-request-number" value={linkReq.number} onChange={e => setLinkReq({
              ...linkReq,
              number: e.target.value
            })} placeholder="e.g. GSNG12092605031234" /></div>
            <div className="space-y-1.5"><Label className="text-base">Email address</Label><Input className="h-12 text-base" type="email" data-testid="claim-request-email" value={linkReq.email} onChange={e => setLinkReq({
              ...linkReq,
              email: e.target.value
            })} /></div>
            <Button className="w-full h-12 text-base bg-[hsl(var(--lux-royal))] text-white" disabled={busy || !linkReq.number || !linkReq.email} onClick={requestLink} data-testid="claim-request-link-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Send me a fresh link</Button>
          </div>}
      </CardContent></Card>);
  }
  if (phase === 'test_done') {
    return shell(<Card className="border-0 shadow-xl rounded-2xl"><CardContent className="p-7 sm:p-9 space-y-5" data-testid="claim-test-complete">
        <Notice tone="good" testId="claim-test-complete-notice"><p className="font-semibold">System test complete</p><p className="mt-1">{error}</p></Notice>
      </CardContent></Card>);
  }
  if (phase === 'confirm') {
    const isDob = intro.confirm_with === 'dob';
    const isEmail = intro.confirm_with === 'email';
    const confirmLabel = isDob ? 'date of birth' : isEmail ? 'email address (the one this link was sent to)' : 'postcode';
    return shell(<ClaimConfirmation busy={busy} confirm={confirm} confirmError={confirmError} confirmLabel={confirmLabel} confirmValue={confirmValue} intro={intro} isDob={isDob} isEmail={isEmail} setConfirmError={setConfirmError} setConfirmValue={setConfirmValue} />);
  }

  // ---------------------------------------------------------------- the 7-step flow
  const s = member.statuses;
  const guardianNeeded = member.is_under_14;
  const guardianOk = !guardianNeeded || !!member.guardian;
  const readyToSubmit = !missing.length && s.photo_id_status !== 'REJECTED' && guardianOk; // photo ID and photograph are optional - they can be added later
  const hasPending = Object.keys(member.pending_identity_changes || {}).length > 0;
  const claimDone = ['SUBMITTED', 'VERIFIED'].includes(s.claim_status);
  return shell(<Card className="border-0 shadow-xl rounded-2xl"><CardContent className="p-5 sm:p-9 space-y-6">
      <Stepper current={step} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm text-[hsl(var(--lux-ink)/0.75)]" data-testid="claim-flow-for">You are claiming membership for: <strong className="text-[hsl(var(--lux-royal))]">{member.full_name || member.first_name}</strong></div>
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--lux-ink)/0.6)] mt-1">Membership number</div>
          <div className="text-xl font-bold tracking-wider text-[hsl(var(--lux-royal))]" data-testid="claim-flow-number">{member.membership_number}</div>
        </div>
        <div className="text-sm text-[hsl(var(--lux-ink)/0.7)]">{member.membership_type}{member.valid_until ? ` · valid until ${member.valid_until}` : ''}</div>
      </div>
      {member.review_note && s.claim_status === 'INFO_REQUESTED' && <Notice tone="warn" testId="claim-review-note"><p className="font-semibold">A note from our team</p><p className="mt-1">{member.review_note}</p></Notice>}

      {step === 1 && <ClaimStep1 form={form} isMissing={isMissing} missing={missing} setStep={setStep} />}

      {step === 2 && <ClaimStep2 busy={busy} detailsError={detailsError} findAddress={findAddress} form={form} guardian={guardian} guardianNeeded={guardianNeeded} isMissing={isMissing} lookup={lookup} member={member} pickAddress={pickAddress} saveDetails={saveDetails} saveGuardian={saveGuardian} setField={setField} setForm={setForm} setGuardian={setGuardian} setStep={setStep} />}

      {step === 3 && <ClaimStep3 busy={busy} claimDone={claimDone} idFiles={idFiles} member={member} s={s} setIdFiles={setIdFiles} setStep={setStep} stepFor={stepFor} upload={upload} />}

      {step === 4 && <ClaimStep4 busy={busy} claimDone={claimDone} member={member} photoFiles={photoFiles} s={s} setPhotoFiles={setPhotoFiles} setStep={setStep} stepFor={stepFor} upload={upload} />}

      {step === 5 && <ClaimStep5 busy={busy} guardianNeeded={guardianNeeded} guardianOk={guardianOk} hasPending={hasPending} member={member} missing={missing} readyToSubmit={readyToSubmit} s={s} setStep={setStep} submit={submit} />}

      {step === 6 && <ClaimStep6 member={member} />}

      {step === 7 && <ClaimStep7 member={member} s={s} setStep={setStep} />}
    </CardContent></Card>);
}

// A new link gets a fresh component instance, so late saves/confirmations cannot populate another person's page.
export default function ClaimMembership() {
  const {
    token
  } = useParams();
  return <ClaimMembershipFlow key={token} />;
}
